/**
 * Oak. Everything about one stamp type in one file: its dimensions, its LOD-0 model, and what it
 * degrades to at each coarser level.
 *
 * THE LADDER (a fine voxel is 0.25 m; a lod-n voxel is 2^n fine):
 *   lod 0  0.25 m   0-40 m    trunk + irregular canopy + 3 lobes, voxel by voxel
 *   lod 1  0.5 m   40-80 m    fattened trunk + one canopy sphere
 *   lod 2  1 m    80-160 m    thin trunk + one smooth cone crown
 *   lod 3  2 m   160-320 m    same, with a shorter trunk overlap so the trunk stays visible
 *   lod 4-5 4-8 m 320m-1.3km  one column per tree, at the tree's own height — the scatter itself
 */
import { mat } from '../../../materials/index.js';
import { groundSink, pinPart } from '../coarseModel.js';
import { conePart, cylinderPart, spherePart, } from '../../../voxel/buildParts.js';
import { MAX_PACKABLE_LOD } from '../../../util/packedKey.js';
import { StampType } from '../StampTypes.js';
import { cylinder, sphere } from '../../../voxel/blobs.js';
/** Fixed trunk height in voxels (4m) - canopy starts here */
const TREE_TRUNK_HEIGHT = 16;
function params(variant) {
    // 50% bigger canopy
    const canopyR = 7.5 + (variant % 3) * 1.5; // 7.5-12 (was 5-8, now 50% bigger)
    const canopyY = TREE_TRUNK_HEIGHT + canopyR * 0.3;
    return {
        trunkH: TREE_TRUNK_HEIGHT,
        trunkTop: canopyY, // into the leaves, as far as the crown's centre
        trunkR: 1.3 + (variant % 2) * 0.2, // 1.3-1.5 - consistent with pine
        canopyR,
        canopyY,
        crownBase: canopyY - canopyR,
        barkMat: variant % 2 === 0 ? mat('bark2') : mat('bark3'),
        leafMat: mat('leaves_trans_3'),
    };
}
/**
 * LOD 0 — the original generateOakTree body, reading its numbers from params.
 *
 * CANOPY FIRST, TRUNK LAST, and the order is the point. Stamp voxels are written in list order and
 * the last write wins, so with the trunk first the canopy OVERWROTE it: measured on the axis column,
 * bark won rows 8-9 and leaf won 10-15, i.e. the canopy ate the top six rows of the trunk. The coarse
 * path composites by max weight with the trunk winning ties, so the two paths disagreed about which
 * part owns the overlap — and the coarse one is the one that looks right, because a trunk visible up
 * inside the foliage is what a tree looks like. This makes LOD 0 agree with it.
 */
function lod0(p, variant) {
    const voxels = [];
    // Canopy - larger irregular sphere centered above trunk
    voxels.push(...sphere(0, Math.floor(p.canopyY), 0, p.canopyR, p.leafMat, 1.2, variant * 7));
    // Add some smaller spheres for irregular shape
    const offsets = [
        { x: p.canopyR * 0.5, z: 0 },
        { x: -p.canopyR * 0.4, z: p.canopyR * 0.3 },
        { x: 0, z: -p.canopyR * 0.5 },
    ];
    for (let i = 0; i < offsets.length; i++) {
        const off = offsets[i];
        voxels.push(...sphere(Math.floor(off.x), Math.floor(p.canopyY + (i - 1) * 1.5), Math.floor(off.z), p.canopyR * 0.6, p.leafMat, 0.8, variant * 13 + i));
    }
    // Trunk last, so it reads through the leaves it penetrates (see the note above).
    voxels.push(...cylinder(0, 0, p.trunkR, 0, p.trunkTop, p.barkMat));
    return voxels;
}
/**
 * lod-1 trunk radius. The real 1.3 fine voxels is 0.65 of a lod-1 voxel — thinner than the grid it
 * would be drawn on, so SurfaceNets discards it. From lod 2 out the trunk becomes a one-voxel column
 * (pinPart): a trunk wide enough to read as a body at those strides would be as wide as the canopy.
 */
const LOD1_TRUNK_R = 2.5;
/** Top of the lod-0 silhouette (the main canopy sphere's crown), which coarser levels must track. */
function lod0Top(p) {
    return p.canopyY + p.canopyR;
}
/** No ceiling: a tree is drawn at every LOD the system can represent. Past lod 3 it is a single
 *  one-voxel column, which stays meaningful at any stride. */
const MAX_LOD = MAX_PACKABLE_LOD;
function lodParts(p, lod) {
    const stride = 1 << lod;
    const top = lod0Top(p);
    if (lod === 1) {
        // The canopy sphere already dips below the trunk top on its own, so the two overlap by
        // construction and no explicit overlap is needed here.
        return [
            cylinderPart(LOD1_TRUNK_R, p.trunkTop + groundSink(stride), p.barkMat, -groundSink(stride)),
            spherePart(p.canopyR * 1.15, p.canopyY, p.leafMat),
        ];
    }
    if (lod === 2 || lod === 3) {
        // One cone for the crown, not a stack: a stack can only change radius in jumps and read as
        // stepped shoulders. An oak's crown is a blob rather than a spire, so unlike the pine its cone
        // keeps a broad top radius — the point is the taper, not a tip.
        const width = lod === 2 ? 1.1 : 1.4;
        return [
            pinPart(p.trunkTop, p.barkMat, stride),
            conePart(p.canopyR * width, Math.max(p.canopyR * 0.6, stride), 
            // The crown's own base (params.crownBase); the trunk running past it to trunkTop supplies the
            // overlap, so no sink fudge is needed here.
            p.crownBase, top, p.leafMat),
        ];
    }
    // lod 4 and out: the tree is narrower than a voxel, so all that is left is a column of the right
    // HEIGHT at the scatter position — one mark per tree, at every remaining level.
    return [pinPart(top, p.leafMat, stride)];
}
export const TREE_OAK_SPEC = {
    type: StampType.TREE_OAK,
    maxLod: MAX_LOD,
    params,
    lod0,
    lodParts,
};
//# sourceMappingURL=treeOak.js.map